import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { ArrowLeft, FileText } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { UserAvatar } from '@/components/ui/user-avatar';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { useConfirm } from '@/components/ui/confirm-dialog';
import { usePermissions } from '@/common/hooks/usePermissions';
import { employeesService, EmployeeContract, Recess, Vacation } from '@/features/employees/services/employees.service';
import { ContractCard } from '@/features/employees/components/ContractCard';
import { ContractFormDialog } from '@/features/employees/components/ContractFormDialog';
import { EmployeeDetail } from '@/features/employees/components/EmployeeDetail';
import { ContractEditDialog } from '@/features/employees/components/ContractEditDialog';
import { RecessSummaryCard } from '@/features/employees/components/RecessSummaryCard';
import { VacationSummaryCard } from '@/features/employees/components/VacationSummaryCard';
import { RecessList } from '@/features/employees/components/RecessList';
import { VacationList } from '@/features/employees/components/VacationList';
import { RecessFormDialog } from '@/features/employees/components/RecessFormDialog';
import { VacationFormDialog } from '@/features/employees/components/VacationFormDialog';
import { PaymentCard } from '@/features/employees/components/PaymentCard';

export const Route = createFileRoute('/_authenticated/employees/$id')({
  component: EmployeeDetailsPage,
});

function EmployeeDetailsPage() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const confirm = useConfirm();
  const { hasPermission } = usePermissions();
  const queryClient = useQueryClient();
  const canViewEmployees = hasPermission('employees.view');
  const canManageEmployees = hasPermission('employees.manage');
  const [contractDialogOpen, setContractDialogOpen] = useState(false);
  const [editContractOpen, setEditContractOpen] = useState(false);
  const [selectedContract, setSelectedContract] = useState<EmployeeContract | null>(null);
  const [employmentType, setEmploymentType] = useState('none');
  const [recessDialogOpen, setRecessDialogOpen] = useState(false);
  const [selectedRecess, setSelectedRecess] = useState<Recess | null>(null);
  const [vacationDialogOpen, setVacationDialogOpen] = useState(false);
  const [selectedVacation, setSelectedVacation] = useState<Vacation | null>(null);

  const { data: employee, isLoading: loadingEmployee } = useQuery({
    queryKey: ['employee', id],
    queryFn: () => employeesService.getEmployee(id),
  });

  const canViewInvoices =
    hasPermission('invoices.manage') && employee?.employmentType === 'pj';

  const { data: contracts = [], isLoading: loadingContracts } = useQuery({
    queryKey: ['employee-contracts', id],
    queryFn: () => employeesService.listEmployeeContracts(id),
  });

  const { data: recessSummary, isLoading: loadingRecessSummary } = useQuery({
    queryKey: ['recess-summary', id],
    queryFn: () => employeesService.getRecessSummary(id),
    enabled: employee?.employmentType === 'pj',
  });

  const { data: recesses = [], isLoading: loadingRecesses } = useQuery({
    queryKey: ['employee-recesses', id],
    queryFn: () => employeesService.listEmployeeRecesses(id),
    enabled: employee?.employmentType === 'pj',
  });

  const { data: vacationSummary, isLoading: loadingVacationSummary } = useQuery({
    queryKey: ['vacation-summary', id],
    queryFn: () => employeesService.getVacationSummary(id),
    enabled: employee?.employmentType === 'clt',
  });

  const { data: vacations = [], isLoading: loadingVacations } = useQuery({
    queryKey: ['employee-vacations', id],
    queryFn: () => employeesService.listEmployeeVacations(id),
    enabled: employee?.employmentType === 'clt',
  });

  useEffect(() => {
    if (employee?.employmentType) {
      setEmploymentType(employee.employmentType);
    }
  }, [employee]);

  const createContractMutation = useMutation({
    mutationFn: ({
      userId,
      data,
      file,
    }: {
      userId: string;
      data: Parameters<typeof employeesService.createContract>[1];
      file: File;
    }) => employeesService.createContract(userId, data, file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employee-contracts', id] });
      toast({ title: 'Contrato adicionado' });
    },
  });

  const updateContractMutation = useMutation({
    mutationFn: ({ contractId, data }: { contractId: string; data: Parameters<typeof employeesService.updateContract>[1] }) =>
      employeesService.updateContract(contractId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employee-contracts', id] });
      toast({ title: 'Contrato atualizado' });
    },
  });

  const updateEmployeeMutation = useMutation({
    mutationFn: (type: string) =>
      employeesService.updateEmployee(id, { employmentType: type as any }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employee', id] });
      queryClient.invalidateQueries({ queryKey: ['employees-list'] });
      queryClient.invalidateQueries({ queryKey: ['employees-select'] });
      toast({ title: 'Tipo de contratação atualizado' });
    },
  });

  const updatePaymentMutation = useMutation({
    mutationFn: (data: {
      payingCompanyId?: string | null;
      paymentDate?: string | null;
      pixKey?: string | null;
      proLabore?: number;
    }) => employeesService.updateEmployee(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employee', id] });
      toast({ title: 'Dados de pagamento atualizados' });
    },
  });

  const createRecessMutation = useMutation({
    mutationFn: (data: Parameters<typeof employeesService.createRecess>[1]) =>
      employeesService.createRecess(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employee-recesses', id] });
      queryClient.invalidateQueries({ queryKey: ['recess-summary', id] });
      toast({ title: 'Recesso adicionado' });
    },
  });

  const updateRecessMutation = useMutation({
    mutationFn: ({ recessId, data }: { recessId: string; data: Parameters<typeof employeesService.updateRecess>[1] }) =>
      employeesService.updateRecess(recessId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employee-recesses', id] });
      queryClient.invalidateQueries({ queryKey: ['recess-summary', id] });
      toast({ title: 'Recesso atualizado' });
    },
  });

  const deleteRecessMutation = useMutation({
    mutationFn: (recessId: string) => employeesService.deleteRecess(recessId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employee-recesses', id] });
      queryClient.invalidateQueries({ queryKey: ['recess-summary', id] });
      toast({ title: 'Recesso removido' });
    },
  });

  const createVacationMutation = useMutation({
    mutationFn: (data: Parameters<typeof employeesService.createVacation>[1]) =>
      employeesService.createVacation(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employee-vacations', id] });
      queryClient.invalidateQueries({ queryKey: ['vacation-summary', id] });
      toast({ title: 'Férias adicionadas' });
    },
  });

  const updateVacationMutation = useMutation({
    mutationFn: ({ vacationId, data }: { vacationId: string; data: Parameters<typeof employeesService.updateVacation>[1] }) =>
      employeesService.updateVacation(vacationId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employee-vacations', id] });
      queryClient.invalidateQueries({ queryKey: ['vacation-summary', id] });
      toast({ title: 'Férias atualizadas' });
    },
  });

  const deleteVacationMutation = useMutation({
    mutationFn: (vacationId: string) => employeesService.deleteVacation(vacationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employee-vacations', id] });
      queryClient.invalidateQueries({ queryKey: ['vacation-summary', id] });
      toast({ title: 'Férias removidas' });
    },
  });

  const handleDownload = async (contractId: string) => {
    try {
      const { url } = await employeesService.getContractDownloadUrl(contractId);
      window.open(url, '_blank');
    } catch {
      toast({
        title: 'Erro ao baixar contrato',
        variant: 'destructive',
      });
    }
  };

  const contractGroups = contracts.reduce((acc, contract) => {
    if (contract.contractType === 'addendum' && contract.parentContractId) {
      acc[contract.parentContractId] = acc[contract.parentContractId] || [];
      acc[contract.parentContractId].push(contract);
      return acc;
    }

    acc[contract.id] = acc[contract.id] || [];
    return acc;
  }, {} as Record<string, typeof contracts>);

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate({ to: '/employees' })}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        {employee && (
          <UserAvatar
            user={{
              id: employee.id,
              email: employee.email,
              fullName: employee.professionalData?.fullName,
              avatarUrl: employee.avatarUrl,
            }}
            className="h-14 w-14 text-base"
          />
        )}
        <div className="flex-1">
          <h1 className="text-3xl font-bold">
            {employee?.professionalData?.fullName || employee?.email || 'Prestador'}
          </h1>
          <p className="text-muted-foreground">
            Tipo de contratação: {employee?.employmentType || '-'}
          </p>
          <div className="mt-2 flex flex-wrap gap-1">
            {employee?.teams && employee.teams.length > 0 ? (
              employee.teams.map((team) => (
                <Badge key={team.id} variant="secondary">
                  {team.name}
                </Badge>
              ))
            ) : (
              <span className="text-sm text-muted-foreground">Sem equipe</span>
            )}
          </div>
        </div>
        {canViewInvoices && (
          <Button asChild variant="outline">
            <Link
              to="/admin/invoices/submitted/$userId"
              params={{ userId: id }}
              search={{ from: 'profile', profileUserId: id }}
            >
              <FileText className="mr-2 h-4 w-4" />
              Notas fiscais
            </Link>
          </Button>
        )}
      </div>

      <EmployeeDetail employee={employee} isLoading={loadingEmployee} />

      {canManageEmployees && (
        <PaymentCard
          payingCompanyId={employee?.professionalData?.payingCompanyId}
          paymentDate={employee?.professionalData?.paymentDate}
          pixKey={employee?.professionalData?.pixKey}
          proLabore={employee?.professionalData?.proLabore}
          employmentType={employee?.employmentType}
          cnpj={employee?.professionalData?.cnpj}
          onSave={(data) => updatePaymentMutation.mutate(data)}
          isLoading={updatePaymentMutation.isPending}
        />
      )}

      <Card>
        <CardHeader>
          <CardTitle>Tipo de contratação</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-4 md:flex-row md:items-end">
          <div className="w-full md:max-w-xs">
            <label className="text-sm font-medium">Selecionar</label>
            <Select value={employmentType} onValueChange={setEmploymentType}>
              <SelectTrigger>
                <SelectValue placeholder="Selecione o tipo" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Nenhuma</SelectItem>
                <SelectItem value="clt">CLT</SelectItem>
                <SelectItem value="pj">PJ</SelectItem>
                <SelectItem value="socio">Sócio</SelectItem>
                <SelectItem value="estagio">Estágio</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button
            onClick={() => updateEmployeeMutation.mutate(employmentType)}
            disabled={updateEmployeeMutation.isPending}
          >
            Salvar
          </Button>
        </CardContent>
      </Card>

      {canViewEmployees && employee?.employmentType === 'pj' && (
        <>
          <RecessSummaryCard summary={recessSummary} isLoading={loadingRecessSummary} />
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Recessos Cadastrados</CardTitle>
              {canManageEmployees && (
                <Button onClick={() => {
                  setSelectedRecess(null);
                  setRecessDialogOpen(true);
                }}>
                  Adicionar Recesso
                </Button>
              )}
            </CardHeader>
            <CardContent>
              {loadingRecesses ? (
                <div className="text-muted-foreground">Carregando recessos...</div>
              ) : (
                <RecessList
                  recesses={recesses}
                  onEdit={canManageEmployees ? (recess) => {
                    setSelectedRecess(recess);
                    setRecessDialogOpen(true);
                  } : undefined}
                  onDelete={canManageEmployees ? async (recessId) => {
                    if (
                      await confirm({
                        title: 'Remover este recesso?',
                        description: 'Esta ação não pode ser desfeita.',
                        confirmLabel: 'Remover recesso',
                        destructive: true,
                      })
                    ) {
                      deleteRecessMutation.mutate(recessId);
                    }
                  } : undefined}
                />
              )}
            </CardContent>
          </Card>
        </>
      )}

      {canViewEmployees && employee?.employmentType === 'clt' && (
        <>
          <VacationSummaryCard summary={vacationSummary} isLoading={loadingVacationSummary} />
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Férias Cadastradas</CardTitle>
              {canManageEmployees && (
                <Button onClick={() => {
                  setSelectedVacation(null);
                  setVacationDialogOpen(true);
                }}>
                  Adicionar Férias
                </Button>
              )}
            </CardHeader>
            <CardContent>
              {loadingVacations ? (
                <div className="text-muted-foreground">Carregando férias...</div>
              ) : (
                <VacationList
                  vacations={vacations}
                  onEdit={canManageEmployees ? (vacation) => {
                    setSelectedVacation(vacation);
                    setVacationDialogOpen(true);
                  } : undefined}
                  onDelete={canManageEmployees ? async (vacationId) => {
                    if (
                      await confirm({
                        title: 'Remover estas férias?',
                        description: 'Esta ação não pode ser desfeita.',
                        confirmLabel: 'Remover férias',
                        destructive: true,
                      })
                    ) {
                      deleteVacationMutation.mutate(vacationId);
                    }
                  } : undefined}
                />
              )}
            </CardContent>
          </Card>
        </>
      )}

      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold">Contratos</h2>
          <p className="text-muted-foreground">
            Gerencie contratos e aditivos do prestador.
          </p>
        </div>
        <Button onClick={() => setContractDialogOpen(true)}>Adicionar contrato</Button>
      </div>

      {loadingContracts ? (
        <div className="text-muted-foreground">Carregando contratos...</div>
      ) : contracts.length === 0 ? (
        <div className="text-muted-foreground">Nenhum contrato cadastrado.</div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {contracts
            .filter((contract) => contract.contractType === 'contract')
            .map((contract) => (
              <ContractCard
                key={contract.id}
                contract={contract}
                addendums={contractGroups[contract.id] || []}
                onDownload={handleDownload}
                onEdit={(item) => {
                  setSelectedContract(item);
                  setEditContractOpen(true);
                }}
              />
            ))}
        </div>
      )}

      <ContractFormDialog
        open={contractDialogOpen}
        onOpenChange={setContractDialogOpen}
        onSubmit={async ({ userId, data, file }) => {
          await createContractMutation.mutateAsync({ userId, data, file });
        }}
        defaultUserId={id}
        contracts={contracts}
      />

      <ContractEditDialog
        open={editContractOpen}
        onOpenChange={setEditContractOpen}
        contract={selectedContract}
        contracts={contracts}
        onSubmit={async (contractId, data) => {
          await updateContractMutation.mutateAsync({ contractId, data });
        }}
      />

      <RecessFormDialog
        open={recessDialogOpen}
        onOpenChange={setRecessDialogOpen}
        recess={selectedRecess}
        onSubmit={async (data) => {
          if (selectedRecess) {
            await updateRecessMutation.mutateAsync({ recessId: selectedRecess.id, data });
          } else {
            await createRecessMutation.mutateAsync(data);
          }
        }}
      />

      <VacationFormDialog
        open={vacationDialogOpen}
        onOpenChange={setVacationDialogOpen}
        vacation={selectedVacation}
        onSubmit={async (data) => {
          if (selectedVacation) {
            await updateVacationMutation.mutateAsync({ vacationId: selectedVacation.id, data });
          } else {
            await createVacationMutation.mutateAsync(data);
          }
        }}
      />
    </div>
  );
}
